import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Solicitation status utilities
export type SolicitationStatus =
	| "draft"
	| "open"
	| "scheduled"
	| "archived"
	| "awaiting_decision"
	| "awarded"
	| "cancelled";

export const getStatusColor = (status: SolicitationStatus | string) => {
	switch (status) {
		case "draft":
			return "bg-gray-100 text-gray-700";
		case "scheduled":
			return "bg-blue-100 text-blue-700";
		case "open":
			return "bg-green-100 text-green-700";
		case "archived":
			return "bg-slate-100 text-slate-700";
		case "awaiting_decision":
			return "bg-yellow-100 text-yellow-700";
		case "awarded":
			return "bg-purple-100 text-purple-700";
		case "cancelled":
			return "bg-red-100 text-red-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

export const getStatusLabel = (status: SolicitationStatus | string) => {
	switch (status) {
		case "draft":
			return "Draft";
		case "scheduled":
			return "Scheduled";
		case "open":
			return "Published";
		case "archived":
			return "Archived";
		case "awaiting_decision":
			return "Pending Award";
		case "awarded":
			return "Awarded";
		case "cancelled":
			return "Cancelled";
		default:
			return status;
	}
};

/**
 * Computes the effective status of a solicitation based on dates.
 * - "scheduled" = status is "open" but publish_date is in the future
 * - "awaiting_decision" = status is "open" but due_date is in the past
 * - Otherwise returns the raw status
 */
export const getEffectiveStatus = (
	status: string,
	publishDate: string | null,
	dueDate: string | null,
): string => {
	if (status === "open") {
		// Check if scheduled (publish_date in future)
		if (publishDate && new Date(publishDate) > new Date()) {
			return "scheduled";
		}
		// Check if awaiting decision (due_date in the past)
		if (dueDate && new Date(dueDate) <= new Date()) {
			return "awaiting_decision";
		}
	}
	return status;
};

// Audit action utilities
export type AuditAction =
	| "create"
	| "update"
	| "delete"
	| "login"
	| "logout"
	| "export"
	| "publish"
	| "award"
	| "invite"
	| "score"
	| "view";

export const getAuditActionColor = (action: AuditAction | string) => {
	switch (action) {
		case "create":
			return "bg-green-100 text-green-700";
		case "update":
			return "bg-blue-100 text-blue-700";
		case "delete":
			return "bg-red-100 text-red-700";
		case "login":
			return "bg-purple-100 text-purple-700";
		case "logout":
			return "bg-gray-100 text-gray-700";
		case "export":
			return "bg-yellow-100 text-yellow-700";
		case "publish":
			return "bg-teal-100 text-teal-700";
		case "award":
			return "bg-orange-100 text-orange-700";
		case "invite":
			return "bg-pink-100 text-pink-700";
		case "score":
			return "bg-indigo-100 text-indigo-700";
		case "view":
			return "bg-slate-100 text-slate-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

export const getAuditActionLabel = (action: AuditAction | string) => {
	switch (action) {
		case "create":
			return "Created";
		case "update":
			return "Updated";
		case "delete":
			return "Deleted";
		case "login":
			return "Logged In";
		case "logout":
			return "Logged Out";
		case "export":
			return "Exported";
		case "publish":
			return "Published";
		case "award":
			return "Awarded";
		case "invite":
			return "Invited";
		case "score":
			return "Scored";
		case "view":
			return "Viewed";
		default:
			return action;
	}
};

// Bid status utilities
export type BidStatus =
	| "submitted"
	| "under_review"
	| "awarded"
	| "rejected"
	| "withdrawn";

export const getBidStatusColor = (status: BidStatus | string) => {
	switch (status) {
		case "submitted":
			return "bg-blue-100 text-blue-700";
		case "under_review":
			return "bg-yellow-100 text-yellow-700";
		case "awarded":
			return "bg-green-100 text-green-700";
		case "rejected":
			return "bg-red-100 text-red-700";
		case "withdrawn":
			return "bg-gray-100 text-gray-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

// Award status utilities
export const getAwardStatusColor = (status: string) => {
	switch (status) {
		case "active":
			return "bg-green-100 text-green-700";
		case "completed":
			return "bg-blue-100 text-blue-700";
		case "pending":
			return "bg-yellow-100 text-yellow-700";
		case "cancelled":
			return "bg-red-100 text-red-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

// Vendor status utilities
export const getVendorStatusColor = (status: string) => {
	switch (status) {
		case "active":
			return "bg-green-100 text-green-700";
		case "inactive":
			return "bg-gray-100 text-gray-700";
		case "pending":
			return "bg-yellow-100 text-yellow-700";
		case "suspended":
			return "bg-red-100 text-red-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

// Notification type utilities
export const getNotificationTypeColor = (type: string) => {
	switch (type) {
		case "bid_received":
			return "bg-blue-100 text-blue-700";
		case "bid_awarded":
			return "bg-green-100 text-green-700";
		case "question_asked":
			return "bg-purple-100 text-purple-700";
		case "deadline_approaching":
			return "bg-yellow-100 text-yellow-700";
		case "solicitation_published":
			return "bg-teal-100 text-teal-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
};

// Types for evaluation
export interface EvaluationCriterion {
	id: string;
	solicitationId: string;
	title: string;
	description: string;
	maxPoints: number;
	sortOrder: number;
}

export interface BestValueWeight {
	id: string;
	solicitationId: string;
	label: string;
	weight: number;
}

export interface QAQuestion {
	id: string;
	solicitationId: string;
	question: string;
	answer?: string;
	askedByVendor?: string;
	askedAt: string;
	answeredBy?: string;
	answeredAt?: string;
	status: "pending" | "answered" | "dismissed";
	isPublic: boolean;
}
